/**
 * Single source of truth for "which profile fields are still missing".
 *
 * Note on the database: `DOB`, `location` and `postal_code` are NOT NULL but
 * all have defaults ('9999-01-01' and ''), so they are sentinel values rather
 * than real data. Only `full_name` is genuinely required.
 */

/** Fields that block the app on sign-in. Everything else is optional/deferrable. */
export const REQUIRED_PROFILE_FIELDS = ['full_name'] as const;

/** DOB sentinel written by the database default when no date was provided. */
export const DOB_SENTINEL = '9999-01-01';

export const isDobMissing = (dob?: string | null): boolean =>
  !dob || dob === DOB_SENTINEL;

export const PROFILE_FIELD_LABELS: Record<string, string> = {
  full_name: 'Full Name',
  DOB: 'Date of Birth',
  location: 'Location',
  postal_code: 'Postal Code',
};

/** All incomplete profile fields (required + optional), sentinel-aware. */
export const getMissingProfileFields = (profileData: any): string[] => {
  const missing: string[] = [];

  if (!profileData?.full_name || String(profileData.full_name).trim() === '') {
    missing.push('full_name');
  }
  if (isDobMissing(profileData?.DOB)) {
    missing.push('DOB');
  }
  if (!profileData?.location || String(profileData.location).trim() === '') {
    missing.push('location');
  }
  if (!profileData?.postal_code || String(profileData.postal_code).trim() === '') {
    missing.push('postal_code');
  }

  return missing;
};

/** Subset of missing fields that must be filled before using the app. */
export const getMissingRequiredProfileFields = (profileData: any): string[] =>
  getMissingProfileFields(profileData).filter((f) =>
    (REQUIRED_PROFILE_FIELDS as readonly string[]).includes(f)
  );

/** True when the user has no usable location data (nearby features degrade). */
export const hasLocationData = (profileData: any): boolean =>
  !!profileData?.location && String(profileData.location).trim() !== '';
